import { Fragment } from 'react';
import { RiArrowRightSLine } from '@remixicon/react';
import { cn } from '@/shared/lib/cn';
import { renderIcon, type IconLike } from './icon';

export interface BreadcrumbItem {
  label: React.ReactNode;
  href?: string;
  onClick?: () => void;
  icon?: IconLike;
}

export interface BreadcrumbsProps {
  items: readonly BreadcrumbItem[];
  /** `sm` 400 14/20 (icon 16) · `tiny` 400 12/16 (icon 12). */
  size?: 'sm' | 'tiny';
  /** "/" White/40 or `arrow-right-s-line`. */
  divider?: 'slash' | 'chevron';
  /** Render links with the router (`<Link>`); receives the item and the classes to apply. */
  renderLink?: (item: BreadcrumbItem, props: { className: string; children: React.ReactNode }) => React.ReactNode;
  'aria-label'?: string;
  className?: string;
}

/** Breadcrumbs — kit 67:11811 (spec §4.11): links White/60, separators White/40, current page White/40, gap 6. */
export function Breadcrumbs({
  items,
  size = 'sm',
  divider = 'slash',
  renderLink,
  'aria-label': ariaLabel = "Sahifa yo'li",
  className,
}: BreadcrumbsProps) {
  const text = size === 'sm' ? 'text-sm' : 'text-xs';
  const icon = size === 'sm' ? 'size-16' : 'size-12';
  return (
    <nav aria-label={ariaLabel} className={className}>
      <ol className={cn('flex flex-wrap items-center gap-6', text)}>
        {items.map((item, i) => {
          const last = i === items.length - 1;
          const inner = (
            <>
              {renderIcon(item.icon, cn(icon, 'shrink-0'))}
              <span className="truncate">{item.label}</span>
            </>
          );
          const linkCls =
            'inline-flex min-w-0 items-center gap-4 rounded-4 text-fg-secondary transition-colors duration-150 hover:text-fg focus-visible:outline-2 focus-visible:outline-accent-2';
          let node: React.ReactNode;
          if (last) {
            node = (
              <span aria-current="page" className="inline-flex min-w-0 items-center gap-4 text-fg-tertiary">
                {inner}
              </span>
            );
          } else if (item.href && renderLink) {
            node = renderLink(item, { className: linkCls, children: inner });
          } else if (item.href) {
            node = (
              <a href={item.href} className={linkCls}>
                {inner}
              </a>
            );
          } else if (item.onClick) {
            node = (
              <button type="button" onClick={item.onClick} className={cn(linkCls, 'cursor-pointer')}>
                {inner}
              </button>
            );
          } else {
            node = <span className="inline-flex min-w-0 items-center gap-4 text-fg-secondary">{inner}</span>;
          }
          return (
            <Fragment key={i}>
              {i > 0 && (
                <li aria-hidden className="flex items-center text-fg-tertiary">
                  {divider === 'slash' ? '/' : <RiArrowRightSLine className={icon} />}
                </li>
              )}
              <li className="flex min-w-0 items-center">{node}</li>
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
